import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useAddNotification } from 'hooks/useAddNotification'
import { BANNER_TYPES, bannersFromServer, bannerToServer } from 'models/campaign-banners-model'
import { URL_API } from 'utils/envHelper'
import { del, get, post, put } from 'utils/http'

// Last path segment of the presigned S3 URL (before the query string) = the asset uuid.
const getUuidFromS3Url = (url: string) => url.split('?')[0].split('/').reverse()[0]

const getUploadUrl = (type: string, customerId: any) => {
  switch (type) {
    case BANNER_TYPES.image:
      return `/customers/${customerId}/campaigns/upload_ad_url`
    case BANNER_TYPES.html5:
      return `/customers/${customerId}/campaigns/upload_html5_banner_url`
    default:
      throw new Error('Banner upload url not found')
  }
}

// The presigned-URL endpoint returns the URL either JSON-quoted or plain text, so fetch + parse-if-JSON-else-raw (utils/http's get does res.json(), which only survives the JSON case).
const fetchUploadUrl = async (path: string): Promise<string> => {
  const res = await fetch(`${URL_API}${path}`, { credentials: 'include' })
  if (!res.ok) throw res
  const raw = await res.text()
  try {
    return JSON.parse(raw)
  } catch {
    return raw
  }
}

// Direct browser -> S3 PUT (no app credentials).
const uploadFile = async (url: string, file: any) => {
  const res = await fetch(url, { method: 'PUT', body: file, credentials: 'omit' })
  if (!res.ok) throw res
}

export const useCampaignBanners = (id: any) =>
  useQuery({
    queryKey: ['campaignBanners', id],
    queryFn: async () => {
      const banners = await get(`/management/campaigns/${id}/banners`)
      return (banners || []).map(bannersFromServer)
    },
    enabled: !!id,
  })

export const useCreateBanner = () =>
  useMutation({
    mutationFn: async ({ id, banner }: any) => {
      const { type, file, ...p } = banner
      let payload

      if (file) {
        const uploadUrl = await fetchUploadUrl(getUploadUrl(type, p.customerId))
        const uuid = getUuidFromS3Url(uploadUrl)
        payload = bannerToServer(p, file, uuid)
        await uploadFile(uploadUrl, file)
      } else {
        payload = bannerToServer(p, file)
      }

      return post(`/management/campaigns/${id}/banners/${type}`, payload)
    },
  })

const bannerCount = (n: number) => `${n} banner${n === 1 ? '' : 's'}`

export const useCreateBanners = () => {
  const createBanner = useCreateBanner()
  const queryClient = useQueryClient()
  const addNotification = useAddNotification()

  return async (id: string, banners: any[]) => {
    const results = await Promise.allSettled(banners.map(banner => createBanner.mutateAsync({ id, banner })))
    const failed = results.filter(r => r.status === 'rejected').length
    const added = results.length - failed

    if (added) addNotification(`${bannerCount(added)} added successfully`)
    if (failed) addNotification(`${bannerCount(failed)} failed to add`, 'error')

    queryClient.invalidateQueries({ queryKey: ['campaignBanners', id] })
  }
}

export const useEditBanner = () => {
  const queryClient = useQueryClient()
  const addNotification = useAddNotification()
  return useMutation({
    mutationFn: async ({ bannerId, campaignId, url, values }: any) => {
      const { type, file, ...p } = values
      if (file) await uploadFile(url, file)
      return put(`/management/campaigns/${campaignId}/banners/${type}/${bannerId}`, bannerToServer(p, file))
    },
    onSuccess: (_d, { campaignId }: any) => {
      addNotification('Banner edited successfully', 'success')
      queryClient.invalidateQueries({ queryKey: ['campaignBanners', campaignId] })
    },
    onError: () => addNotification('Banner update error', 'error'),
  })
}

export const useDeleteBanner = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ bannerId, campaignId, type }: any) =>
      del(`/management/campaigns/${campaignId}/banners/${type}/${bannerId}`),
    onSettled: (_d, _e, { campaignId }: any) =>
      queryClient.invalidateQueries({ queryKey: ['campaignBanners', campaignId] }),
  })
}
